import { useEffect, useState } from 'react';
import { PreferenceClient } from '../client/preference.client';
import { Preference, PreferenceName, ValueSet } from '../types';
import { CustomPriceClass, SavedCustomPriceClass } from '../types/content/custom.types';
import { getPriceClassesFromSettings, isViewer } from './utils';
import { useUniquePreferences } from '../context/unique-preferences/unique-preferences.provider';
import { DEFAULT_QUANTITY } from '../elements/constants';

/**
 * Hook to save and load the saved custom price classes
 * from the preference client
 * @returns - the saved price classes and the functions to save and load the price classes
 */
export const useSavePriceClasses = () => {
  const preferencesCustomPriceClassesName = PreferenceName.segment_price;

  const [savedPriceClasses, setSavedPriceClasses] = useState<SavedCustomPriceClass[]>([]);
  const [currentActive, setCurrentActive] = useState<string>('');
  const { allUniquePreferences, refreshPreferences } = useUniquePreferences();

  useEffect(() => {
    loadSavedPriceClasses();
  }, []);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences, savedPriceClasses]);

  /**
   * Convert the response from the preference client
   * to the format that the Custom Price Classes will use
   * @param data - the response returned from the preference client
   * @returns - the formatted price classes
   */
  const convertPreferenceDataToPriceClasses = (data: Preference): SavedCustomPriceClass | undefined => {
    if (!data.data || Object.keys(data.data).length === 0) {
      return undefined;
    }

    const prefName = Object.keys(data.data)[0];
    const prefData = data.data[prefName];
    const currency = prefData.currency[0];
    const roundTo = Number(prefData.roundTo[0]);
    const description = prefData.description[0];
    const qunatityType = prefData.quantityType?.[0] ? prefData.quantityType[0] : DEFAULT_QUANTITY;

    const formattedPriceList = getPriceClassesFromSettings(
      { customPriceTiers: prefData.priceClasses },
      { label: '', min: 0, max: roundTo, currency, roundTo, quantityType: qunatityType }
    );

    return {
      name: prefName,
      description,
      priceClasses: formattedPriceList,
      preferenceId: data.id,
      createdBy: data.created_by,
    };
  };

  /**
   * Convert the custom price classes to the format that the preference client will use
   * @param desc - the description of the saved custom price class
   * @param priceClasses - the list of saved custom price class
   * @returns - the formatted data
   */
  const convertPriceClassesToPreferenceData = (desc: string, priceClasses: CustomPriceClass[]) => {
    const currency = priceClasses[0].currency;
    const roundTo = priceClasses[0].roundTo;
    const quantityType = priceClasses[0].quantityType;
    let tierValues = '';
    priceClasses.forEach((item, index, list) => {
      tierValues += `${item.min}:${item.label}${index < list.length - 1 ? '|' : ''}`;
    });

    return {
      currency: [currency],
      roundTo: [roundTo.toString()],
      description: [desc],
      priceClasses: [tierValues],
      quantityType: [quantityType],
    };
  };

  /**
   * Save the new saved price class to the preference client
   * @param name - the name of the new saved price class
   * @param desc - the description of the new saved price classes
   * @param customPriceClasses - the custom price classes
   */
  const savePriceClasses = (name: string, desc: string, customPriceClasses: CustomPriceClass[]) => {
    const priceClassToSave: ValueSet = {};
    priceClassToSave[name] = convertPriceClassesToPreferenceData(desc, customPriceClasses);

    PreferenceClient.getInstance()
      .createNewUnique(preferencesCustomPriceClassesName, priceClassToSave)
      .then(response => {
        if (!response) {
          return;
        }
        const priceClasses = convertPreferenceDataToPriceClasses(response);
        setSavedPriceClasses([...savedPriceClasses, priceClasses]);
      });
  };

  /**
   * Delete the saved price classes filter in preference client
   * @param savedPriceClass - the saved price classes to delete
   * @param userId - the userId of the user
   */
  const deleteSavedPriceClasses = async (savedPriceClass: SavedCustomPriceClass, userId: string) => {
    // if the user is the viewer, delete the permissions, otherwise delete the whole preference
    if (isViewer(savedPriceClass.createdBy, userId)) {
      await PreferenceClient.getInstance().deletePermissions(
        preferencesCustomPriceClassesName,
        savedPriceClass.preferenceId,
        userId
      );
    } else {
      await PreferenceClient.getInstance().delete(preferencesCustomPriceClassesName, savedPriceClass.preferenceId);
    }
    const priceClasses = savedPriceClasses.filter(spc => spc.preferenceId != savedPriceClass.preferenceId);
    setSavedPriceClasses(priceClasses);
    return priceClasses;
  };

  /**
   * Load the saved price classes from the preference client
   * and store it within the states
   */
  const loadSavedPriceClasses = () => {
    const priceClasses: SavedCustomPriceClass[] = [];
    if (allUniquePreferences[preferencesCustomPriceClassesName]?.length) {
      allUniquePreferences[preferencesCustomPriceClassesName].forEach(preference => {
        const priceClass = convertPreferenceDataToPriceClasses(preference);
        if (priceClass) {
          priceClasses.push(priceClass);
        }
      });
    }
    setSavedPriceClasses(priceClasses);
  };

  /**
   * Set the active saved price classes
   * Called when the user apply a saved price classes
   * @param preferenceId - the preferenceId of the saved price classes
   */
  const setIsActive = (preferenceId: string) => {
    const index = savedPriceClasses.findIndex(item => item.preferenceId === preferenceId);
    setCurrentActive(index > -1 ? preferenceId : '');
  };

  return {
    savedPriceClasses,
    currentActive,
    savePriceClasses,
    loadSavedPriceClasses,
    setIsActive,
    deleteSavedPriceClasses,
    convertPriceClassesToPreferenceData,
    convertPreferenceDataToPriceClasses,
  };
};
